import React, { useState, useEffect } from "react";

import { Tooltip, Button, Chip } from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import ReportNoteSectionModal from "./ReportNoteSectionModal";
import ConfirmModal from "components/common/modals/confirmModal/ConfirmModal";

import usePageSizeOptions from "hooks/usePageSizeOptions";
import {
  getReportNoteSections,
  deleteReportNoteSection,
} from "app/services/report_templates/reportNoteSectionService";
import CustomTable from "components/common/table/CustomTable";
import { showErrorToast, showSuccessToast } from "utils/toast";
import { MRT_GlobalFilterTextField as GlobalFilterTextField } from "material-react-table";
import { Grid } from "@mui/material";
import { textFilter } from "utils/table";

const ReportNoteSectionsView = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [reportNoteSections, setReportNoteSections] = useState([]);
  const [, , pageSizeOptions] = usePageSizeOptions(reportNoteSections?.length);

  const fetchReportNoteSections = async () => {
    try {
      setIsLoading(true);

      const data = await getReportNoteSections();
      setReportNoteSections(data || []);
    } catch (error) {
      showErrorToast(error?.message ?? error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReportNoteSections();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteNoteSection = async () => {
    try {
      setIsDeleting(true);
      await deleteReportNoteSection(selectedRow?._id);
      await fetchReportNoteSections();
      showSuccessToast("Report Note Section deleted successfully");
    } catch (error) {
      showErrorToast(error?.message ?? error);
    } finally {
      setIsDeleting(false);
      setSelectedRow(null);
      setShowConfirmModal(false);
    }
  };

  const onCloseModal = () => {
    setShowEditModal(false);
    setIsEditMode(false);
    setSelectedRow(null);
  };

  const onConfirmModalClose = () => {
    setShowConfirmModal(false);
    setSelectedRow(null);
  };

  const columns = [
    {
      header: "Report Note Section ID",
      accessorKey: "_id",
      filterVariant: "text",
    },
    {
      header: "Note",
      accessorKey: "note",
      filterVariant: "text",
      filterFn: textFilter,
    },
    {
      header: "Associated Note",
      accessorKey: "associated_note",
      filterVariant: "text",
      filterFn: textFilter,
    },
    {
      header: "Account",
      accessorKey: "account",
      type: "text",
      filterFn: textFilter,
      size: 200,
    },
    {
      header: "Placeholders",
      accessorKey: "placeholders",
      size: 500,
      enableColumnFilter: false,
      accessorFn: (rowData) => {
        const placeholders = rowData.placeholders?.filter(Boolean);
        return (
          <div className="d-flex flex-wrap">
            {placeholders?.map((placeholder, index) => (
              <Chip
                key={index}
                label={placeholder}
                sx={{
                  maxWidth: "100%",
                  height: "auto",
                  "& .MuiChip-label": {
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  },
                }}
              />
            ))}
          </div>
        );
      },
    },
    {
      header: "Actions",
      accessorKey: "actions",
      size: 20,
      enableSorting: false,
      enableColumnFilter: false,
      accessorFn: (rowData) => {
        return (
          <div style={{ display: "inline-flex" }}>
            <Tooltip header={"Edit"}>
              <EditIcon
                className="editIcon"
                style={{ marginRight: "10px" }}
                onClick={() => {
                  setShowEditModal(true);
                  setIsEditMode(true);
                  setSelectedRow(rowData);
                }}
              />
            </Tooltip>

            <Tooltip header={"Delete"}>
              <DeleteIcon
                className="deleteIcon"
                onClick={() => {
                  setShowConfirmModal(true);
                  setSelectedRow(rowData);
                }}
              />
            </Tooltip>
          </div>
        );
      },
    },
  ];

  return (
    <>
      {showConfirmModal && (
        <ConfirmModal
          show={showConfirmModal}
          isConfirming={isDeleting}
          onConfirm={deleteNoteSection}
          onCancel={onConfirmModalClose}
          confirmButtonProps={{
            variant: "contained",
            style: { color: "fff", backgroundColor: "#f44336" },
          }}
        />
      )}
      {showEditModal && (
        <ReportNoteSectionModal
          show={showEditModal}
          isEditMode={isEditMode}
          selectedRow={selectedRow}
          onCloseModal={onCloseModal}
          fetchReportNoteSections={fetchReportNoteSections}
        />
      )}

      <CustomTable
        data={reportNoteSections ?? []}
        columns={columns}
        options={{
          enableTopToolbar: true,
          enableSorting: true,
          state: {
            isLoading,
            showGlobalFilter: true,
            columnVisibility: { _id: false },
            showColumnFilters: true,
          },
          rowsPerPageOptions: pageSizeOptions,
          muiSearchTextFieldProps: {
            placeholder: "Search Report Notes",
          },
          renderTopToolbar: ({ table }) => (
            <Grid
              style={{
                display: "flex",
                gap: "10px",
                padding: "10px 15px",
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              <GlobalFilterTextField
                table={table}
                props={{ placeholder: "Search Templates" }}
              />
              <Button
                onClick={() => {
                  setShowEditModal(true);
                  setIsEditMode(false);
                }}
                variant="contained"
                color="secondary"
                startIcon={<AddIcon />}
              >
                Add Report Note
              </Button>
            </Grid>
          ),
        }}
      />
    </>
  );
};

export default React.memo(ReportNoteSectionsView);
